// src/services/patientSurveyService.js
//
// I/O for the health survey sent from the patient page (rules: patientSurvey.js and
// functions/pftSurveyPatient.js). Every write goes through a function; the browser
// only reads the status doc and the patient's PFT imports.

import { collection, doc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { getCollectionPath } from "./firestorePaths";
import { getActiveSiteId, getActiveTenantId } from "./activeTenant";
import { authedFetch } from "./authedFetch";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { cardSurveyFor } from "./patientSurvey";

const statusId = ({ store, id }) => `${store}__${id}`;

/** Live status of this patient's survey (null when never texted). Returns unsubscribe. */
export const watchPatientSurveyStatus = (patient, onChange, onError) =>
  onSnapshot(
    doc(db, getCollectionPath("pft_survey_patient_status"), statusId(patient)),
    (snap) => onChange(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    onError,
  );

/**
 * Status doc ids (`${store}__${id}`) of every patient at this site who answered the
 * survey text, for the CRC page's green cards. One read. Admins and coordinators
 * only by rule; anyone else (reception) gets a refusal, which the page ignores.
 */
export const loadRespondedSurveyKeys = async () => {
  const snap = await getDocs(
    query(collection(db, getCollectionPath("pft_survey_patient_status")), where("status", "==", "responded"))
  );
  return new Set(snap.docs.map((d) => d.id));
};

/** A PFT Uploads card texted before this patient was moved or merged, or null. */
export const loadCardSurveyForPatient = async (patient) => {
  const snap = await getDocs(query(collection(db, getCollectionPath("pft_imports")), where("patientId", "==", patient.id)));
  return cardSurveyFor(
    snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    patient,
  );
};

const post = async (name, body) => {
  const res = await authedFetch(`${FUNCTIONS_BASE}/${name}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tenantId: getActiveTenantId(), siteId: getActiveSiteId(), ...body }),
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok) throw new Error(data.error || "Something went wrong. Please try again.");
  return data;
};

/** The exact text that would be sent ({ body, reason, phoneLast4 }); nothing is sent. */
export const previewPatientSurveyText = (patient, { resend = false } = {}) =>
  post("sendPatientSurveySms", { patient, resend, preview: true });

/** Send it (only ever on a person's click). */
export const sendPatientSurveyText = (patient, { resend = false } = {}) => post("sendPatientSurveySms", { patient, resend });

/** "Use survey" / "Keep RegX" on one difference. `from` = the value the page shows now. */
export const resolvePatientSurveyAnswer = (patient, { field, choice, from }) =>
  post("resolvePftSurveyAnswer", { patient, field, choice, from });

/** Attach answers that couldn't be attached automatically. */
export const attachPatientSurveyAnswers = (patient) => post("attachPftSurveyAnswers", { patient });
